// src/features/dashboard/components/EmployeeActiveHoursReport/WTDActiveHoursTrend.tsx

import { useState } from 'react';

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
} from 'recharts';

import {
  WtdActiveHoursResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

interface Props {
  data: WtdActiveHoursResponse[];
}

interface ChartDataItem {
  day: string;
  active: number;
  target: number;
}

/* ============================================================================
   CONSTANTS
   ========================================================================== */

const TARGET_HOURS = 7.5;

/* ============================================================================
   CONVERT HH:mm:ss TO DECIMAL HOURS
   ========================================================================== */

const convertToHours = (
  time: string,
): number => {
  if (!time) {
    return 0;
  }

  const parts = time
    .split(':')
    .map(Number);

  const hours =
    parts[0] || 0;

  const minutes =
    parts[1] || 0;

  const seconds =
    parts[2] || 0;

  return (
    hours +
    minutes / 60 +
    seconds / 3600
  );
};

/* ============================================================================
   FORMAT DAY + DATE

   Monday + 2026-08-17T00:00:00
   ↓
   Mon 17
   ========================================================================== */

const formatDayWithDate = (
  dayName?: string,
  workDate?: string,
): string => {
  const shortDay = dayName
    ? dayName.substring(0, 3)
    : '';

  if (!workDate) {
    return shortDay;
  }

  const datePart =
    workDate.split('T')[0];

  const dateParts =
    datePart.split('-');

  const dateNumber =
    dateParts.length === 3
      ? dateParts[2]
      : '';

  if (!shortDay) {
    return dateNumber;
  }

  if (!dateNumber) {
    return shortDay;
  }

  return `${shortDay} ${dateNumber}`;
};

/* ============================================================================
   TARGET LINE LABEL
   ========================================================================== */

interface TargetLabelProps {
  viewBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
}

/**
 * Small target badge shown directly above
 * the dashed target reference line.
 */

const TargetLabel = ({
  viewBox,
}: TargetLabelProps) => {
  if (!viewBox) {
    return null;
  }

  const x =
    Number(viewBox.x ?? 0);

  const y =
    Number(viewBox.y ?? 0);

  const width =
    Number(viewBox.width ?? 0);

  const badgeWidth = 68;

  const badgeHeight = 20;

  /*
   * Keep badge close to the right
   * side of the chart.
   */

  const badgeX =
    x +
    width -
    badgeWidth -
    2;

  /*
   * Show badge ABOVE the target line.
   */

  const badgeY =
    y -
    badgeHeight -
    3;

  return (
    <g
      pointerEvents="none"
    >
      <rect
        x={badgeX}
        y={badgeY}
        width={badgeWidth}
        height={badgeHeight}
        rx={10}
        ry={10}
        fill="#FFF5F5"
        stroke="#FF4D4F"
        strokeWidth={0.8}
      />

      <text
        x={
          badgeX +
          badgeWidth / 2
        }
        y={
          badgeY +
          badgeHeight / 2 +
          3.5
        }
        textAnchor="middle"
        fill="#EF4444"
        fontSize={10}
        fontWeight={500}
      >
        {`${TARGET_HOURS}h target`}
      </text>
    </g>
  );
};

/* ============================================================================
   TOOLTIP
   ========================================================================== */

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  hoveredBar?: 'active' | 'target' | null;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  hoveredBar,
}: CustomTooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0 ||
    !hoveredBar
  ) {
    return null;
  }

  /*
   * Select only the bar which is actually hovered.
   */

  const item =
    payload.find(
      (entry: any) =>
        entry.dataKey ===
        hoveredBar,
    );

  if (
    !item ||
    item.value === undefined ||
    item.value === null
  ) {
    return null;
  }

  const isActive =
    hoveredBar === 'active';

  const color =
    isActive
      ? '#7C4DFF'
      : '#E5E7EB';

  const title =
    isActive
      ? 'Active Hrs'
      : 'Target';

  return (
    <div
      className="
        pointer-events-none
        min-w-[155px]
        overflow-hidden
        rounded-xl
        border
        border-[#D0D5DD]
        bg-white
        shadow-xl
      "
    >
      {/* Tooltip Header */}

      <div
        className="
          border-b
          border-[#EAECF0]
          bg-[#F8FAFC]
          px-3
          py-2
        "
      >
        <span
          className="
            text-[13px]
            font-semibold
            text-[#344054]
          "
        >
          {label}
        </span>
      </div>

      {/* Tooltip Value */}

      <div
        className="
          flex
          items-center
          justify-between
          gap-4
          px-3
          py-3
        "
      >
        <div
          className="
            flex
            items-center
            gap-2
          "
        >
          <span
            className="
              h-3
              w-3
              shrink-0
              rounded-full
            "
            style={{
              backgroundColor:
                color,
            }}
          />

          <span
            className="
              whitespace-nowrap
              text-[13px]
              text-[#475467]
            "
          >
            {title}
          </span>
        </div>

        <span
          className="
            whitespace-nowrap
            text-[13px]
            font-semibold
            text-[#111827]
          "
        >
          {Number(
            item.value,
          ).toFixed(1)}
          h
        </span>
      </div>
    </div>
  );
};

/* ============================================================================
   COMPONENT
   ========================================================================== */

const WTDActiveHoursTrend = ({
  data,
}: Props) => {
  const [
    activeSeries,
    setActiveSeries,
  ] = useState<
    'active' | 'target' | null
  >(null);

  /*
   * Tracks the exact bar currently hovered.
   */

  const [
    hoveredBar,
    setHoveredBar,
  ] = useState<
    'active' | 'target' | null
  >(null);

  /* ==========================================================================
     CHART DATA
     ========================================================================== */

  const chartData: ChartDataItem[] =
    Array.isArray(data)
      ? data.map(
          (item) => ({
            day:
              formatDayWithDate(
                item.dayName,
                item.workDate,
              ),

            active:
              convertToHours(
                item.activeHours,
              ),

            target:
              TARGET_HOURS,
          }),
        )
      : [];

  /* ==========================================================================
     DYNAMIC Y AXIS
     ========================================================================== */

  const maxValue =
    Math.max(
      ...chartData.flatMap(
        (item) => [
          Number(
            item.active,
          ) || 0,

          Number(
            item.target,
          ) || 0,
        ],
      ),
      0,
    );

  /*
   * Dynamic Y-axis maximum.
   *
   * Minimum = 9
   */

  const yAxisMax =
    Math.max(
      9,
      Math.ceil(
        maxValue / 3,
      ) * 3,
    );

  /* ==========================================================================
     DYNAMIC TICKS
     ========================================================================== */

  const yAxisTicks =
    Array.from(
      {
        length:
          Math.floor(
            yAxisMax / 3,
          ) + 1,
      },
      (_, index) =>
        index * 3,
    );

  /* ==========================================================================
     EMPTY STATE
     ========================================================================== */

  if (!chartData.length) {
    return (
      <div
        className="
          w-full
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        <div
          className="
            mb-4
            flex
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[15px]
              font-semibold
              text-[#002B5B]
            "
          >
            WTD Active Hours Trend
          </h3>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-3
              py-[4px]
              text-[10px]
              font-medium
              text-[#2563EB]
            "
          >
            Daily active hours vs 7.5h target
          </span>
        </div>

        <div
          className="
            flex
            h-[220px]
            items-center
            justify-center
            text-[13px]
            text-[#667085]
          "
        >
          No active hours data available
        </div>
      </div>
    );
  }

  /* ==========================================================================
     UI
     ========================================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        overflow-visible
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ======================================================================
          HEADER
          ====================================================================== */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          WTD Active Hours Trend
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          Daily active hours vs 7.5h target
        </span>
      </div>

      {/* ======================================================================
          LEGEND
          ====================================================================== */}

      <div
        className="
          mb-3
          flex
          justify-center
        "
      >
        <div
          className="
            flex
            flex-wrap
            items-center
            justify-center
            gap-x-8
            gap-y-2
          "
        >
          {/* Active Hours */}

          <div
            className="
              flex
              cursor-pointer
              items-center
              gap-2
            "
            onMouseEnter={() =>
              setActiveSeries(
                'active',
              )
            }
            onMouseLeave={() =>
              setActiveSeries(null)
            }
          >
            <span
              className="
                h-[10px]
                w-[10px]
                rounded-[2px]
              "
              style={{
                backgroundColor:
                  activeSeries ===
                  'target'
                    ? '#C4B5FD'
                    : '#7C4DFF',
              }}
            />

            <span
              className={`
                text-[12px]
                ${
                  activeSeries ===
                  'active'
                    ? 'font-semibold text-[#7C4DFF]'
                    : 'text-[#475467]'
                }
              `}
            >
              Active Hrs
            </span>
          </div>

          {/* Target */}

          <div
            className="
              flex
              cursor-pointer
              items-center
              gap-2
            "
            onMouseEnter={() =>
              setActiveSeries(
                'target',
              )
            }
            onMouseLeave={() =>
              setActiveSeries(null)
            }
          >
            <span
              className="
                h-[10px]
                w-[10px]
                rounded-[2px]
              "
              style={{
                backgroundColor:
                  activeSeries ===
                  'active'
                    ? '#D1D5DB'
                    : '#E5E7EB',
              }}
            />

            <span
              className={`
                text-[12px]
                ${
                  activeSeries ===
                  'target'
                    ? 'font-semibold text-[#6B7280]'
                    : 'text-[#475467]'
                }
              `}
            >
              Target
            </span>
          </div>
        </div>
      </div>

      {/* ======================================================================
          CHART
          ====================================================================== */}

      <div
        className="
          relative
          w-full
          min-w-0
          overflow-visible
        "
      >
        <ResponsiveContainer
          width="100%"
          height={220}
          minWidth={0}
        >
          <ComposedChart
            data={chartData}
            margin={{
              top: 28,
              right: 12,
              left: 4,
              bottom: 4,
            }}

            /*
             * Space between different days.
             *
             * KEEP THIS AS IT IS.
             */
            barCategoryGap="40%"

            /*
             * IMPORTANT:
             *
             * This controls the space BETWEEN
             * Active Hrs and Target bars.
             *
             * Previously:
             *
             * barGap={10}
             *
             * Now:
             *
             * barGap={2}
             *
             * This makes the two bars sit
             * closely beside each other.
             */
            barGap={2}
          >
            {/* =================================================================
                GRID
                ================================================================= */}

            <CartesianGrid
              vertical={false}
              stroke="#EAECF0"
            />

            {/* =================================================================
                X AXIS
                ================================================================= */}

            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              interval={0}
              tick={{
                fontSize: 11,
                fill: '#475467',
              }}
            />

            {/* =================================================================
                Y AXIS
                ================================================================= */}

            <YAxis
              domain={[
                0,
                yAxisMax,
              ]}
              ticks={
                yAxisTicks
              }
              width={42}
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              tickFormatter={(
                value,
              ) =>
                `${value}h`
              }
              tick={{
                fontSize: 11,
                fill: '#475467',
              }}
            />

            {/* =================================================================
                TARGET REFERENCE LINE + LABEL
                ================================================================= */}

            <ReferenceLine
              y={
                TARGET_HOURS
              }
              stroke="#EF4444"
              strokeWidth={1}
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={
                <TargetLabel />
              }
            />

            {/* =================================================================
                TOOLTIP
                ================================================================= */}

            <Tooltip
              shared={false}
              cursor={false}
              wrapperStyle={{
                outline:
                  'none',
                zIndex: 1000,
              }}
              content={
                <CustomTooltip
                  hoveredBar={
                    hoveredBar
                  }
                />
              }
            />

            {/* =================================================================
                ACTIVE HOURS BAR
                ================================================================= */}

            <Bar
              dataKey="active"
              name="Active Hrs"
              barSize={18}
              maxBarSize={20}
              fill="#7C4DFF"
              fillOpacity={
                activeSeries ===
                'target'
                  ? 0.35
                  : 1
              }
              radius={[
                3,
                3,
                0,
                0,
              ]}
              activeBar={{
                fill: '#6D3FE8',
                opacity: 1,
              }}
              onMouseEnter={() => {
                setHoveredBar(
                  'active',
                );
              }}
              onMouseLeave={() => {
                setHoveredBar(
                  null,
                );
              }}
            />

            {/* =================================================================
                TARGET BAR
                ================================================================= */}

            <Bar
              dataKey="target"
              name="Target"
              barSize={18}
              maxBarSize={20}
              fill="#E5E7EB"
              fillOpacity={
                activeSeries ===
                'active'
                  ? 0.35
                  : 1
              }
              radius={[
                3,
                3,
                0,
                0,
              ]}
              activeBar={{
                fill: '#D1D5DB',
                opacity: 1,
              }}
              onMouseEnter={() => {
                setHoveredBar(
                  'target',
                );
              }}
              onMouseLeave={() => {
                setHoveredBar(
                  null,
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WTDActiveHoursTrend;